import { ExternalLink, Youtube } from "lucide-react";

/**
 * Resilient YouTube embed.
 *
 * Why this exists:
 *   - Some videos may have YouTube's "Allow embedding" toggle disabled in Studio,
 *     which makes the iframe show "Video unavailable" with no way to tell client-side
 *     (cross-origin iframe = no error event).
 *   - We therefore ALWAYS render a prominent "Watch on YouTube" CTA below the embed so
 *     the visitor has an obvious one-click route to the actual video, regardless.
 *   - We use the standard `youtube.com/embed/` domain (NOT youtube-nocookie) plus
 *     `origin=` and `playsinline=1` to avoid YouTube's bot-challenge gating that was
 *     hitting nocookie embeds in mid-2026.
 */
export const YouTubeEmbed = ({ videoId, title = "Video", autoplay = false, className = "" }) => {
  if (!videoId) return null;
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const params = new URLSearchParams({
    autoplay: autoplay ? "1" : "0",
    rel: "0",
    modestbranding: "1",
    playsinline: "1",
    enablejsapi: "1",
  });
  if (origin) params.set("origin", origin);
  const src = `https://www.youtube.com/embed/${videoId}?${params.toString()}`;
  const watchUrl = `https://www.youtube.com/watch?v=${videoId}`;

  return (
    <div className={`w-full bg-black ${className}`}>
      <div className="relative aspect-video w-full">
        <iframe
          data-testid="youtube-embed-iframe"
          src={src}
          title={title}
          className="absolute inset-0 h-full w-full"
          loading="lazy"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
        />
      </div>
      {/* Always-visible CTA bar below the iframe - works even if YouTube blocks the embed */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 px-4 py-3 border-t border-border bg-[hsl(225,22%,4%)]">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Now playing</div>
          <div className="text-sm text-foreground truncate">{title}</div>
        </div>
        <a
          href={watchUrl}
          target="_blank"
          rel="noreferrer noopener"
          data-testid="youtube-embed-fallback"
          className="inline-flex items-center gap-2 rounded-full bg-[hsl(var(--primary))] hover:brightness-110 text-[hsl(var(--primary-foreground))] text-xs uppercase tracking-[0.18em] px-4 py-2 transition-all whitespace-nowrap"
        >
          <Youtube className="h-4 w-4" />
          Watch on YouTube
          <ExternalLink className="h-3 w-3" />
        </a>
      </div>
    </div>
  );
};

export default YouTubeEmbed;
